/**
 * Add-On Prompt Platforms
 *
 * Every tracked prompt run covers a BASE set of platforms at no extra cost to
 * us: ChatGPT, Gemini, and Google AI Overviews all go through DataForSEO's
 * task API, which is cheap and priced per task.
 *
 * Claude and Perplexity have no DataForSEO endpoint. To track them we call the
 * Anthropic / Perplexity APIs directly, once per prompt per location per night
 * — real inference spend that scales with the whole tracked fleet, not with
 * what the customer paid. So they are ADD-ONS: off by default, switched on per
 * property by an admin (`Property.extraPromptPlatforms`) when the account has
 * paid for them.
 *
 * This module is browser-safe on purpose — the admin UI reads the same
 * allowlist and labels the dispatch validates against, so the two can't drift.
 * Do NOT import the AI SDK providers (see `llm-platforms.ts`) from here.
 *
 * Prompt-run billing is per unit per night regardless of platform count, so
 * enabling an add-on does not change what the customer's credits are debited
 * for — the add-on fee is charged separately, outside this gate.
 *
 * ⚠️ PUBLIC DOCS DEPENDENCY: which platforms are base vs. add-on is published
 * in the platform table and the "How Often Prompts Run" section of
 *   apps/web/content/docs/methodology/ai-visibility-data-collection.mdx
 * (and its .de.mdx counterpart), plus the daily-coverage claims in
 * `marketing.promptTracking` / `homeV2.strip` in the i18n translations. Moving a
 * platform between the two sets means updating those in the same PR.
 */

/**
 * Platforms an admin can switch on for a property, keyed by the same
 * identifiers `DirectRunPlatform` / `LLM_PLATFORM_CONFIGS` use.
 *
 * Adding a platform here makes it selectable in the admin UI and dispatchable
 * by the nightly/day-1 jobs — it must already be executable through
 * `executePromptRunDirect`.
 */
export const ADD_ON_PROMPT_PLATFORMS = ["claude", "perplexity"] as const;

export type AddOnPromptPlatform = (typeof ADD_ON_PROMPT_PLATFORMS)[number];

/** Display labels for the admin UI. */
export const ADD_ON_PROMPT_PLATFORM_LABELS: Record<
	AddOnPromptPlatform,
	string
> = {
	claude: "Claude",
	perplexity: "Perplexity",
};

export function isAddOnPromptPlatform(
	value: string,
): value is AddOnPromptPlatform {
	return (ADD_ON_PROMPT_PLATFORMS as readonly string[]).includes(value);
}

/**
 * Read a property's stored add-on list into a validated, deduped, canonically
 * ordered platform set.
 *
 * Unknown values are dropped rather than thrown on: the column is a plain
 * String[] an admin wrote, and one stale entry must never take down a nightly
 * dispatch for every property in the fleet.
 */
export function resolveAddOnPromptPlatforms(
	stored: string[] | null | undefined,
): AddOnPromptPlatform[] {
	if (!stored?.length) return [];
	const enabled = new Set(stored.filter(isAddOnPromptPlatform));
	return ADD_ON_PROMPT_PLATFORMS.filter((platform) => enabled.has(platform));
}
